import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { INBOX_ALL_HEADER_TOTAL_PX, INBOX_HEADER_BORDER_PX } from "../../utils/inboxLayoutConstants";
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Skeleton,
  Badge,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  MarkChatUnreadOutlined as MarkChatUnreadOutlinedIcon,
} from "@mui/icons-material";
// Same icon InboxSettings.jsx's own panel-heading toggle uses (SidebarLeft) —
// one consistent open/close icon language instead of a generic hamburger.
import { SidebarLeft as SidebarToggleIconIx, Tag as TagIconIx } from "iconsax-react";
import { DROPDOWN_ITEM_SX } from "../../utils/dropdownMenuStyles";
// import { ReactComponent as LeftScroll } from "../../Images/Inbox/ActiveMemberLeftScroll.svg";
import { ReactComponent as FilterButton } from "../../Images/Inbox/filter-button.svg";
// import { ReactComponent as RightScroll } from "../../Images/Inbox/ActiveMemberRightScroll.svg";
import MessageCard from "./Features/MessageCard";
import CallsSectionBody from "./CallsSection/CallsSectionBody";
import InboxSearchToggle from "./Shared/InboxSearchToggle";
import InboxFilterDropdown from "./Shared/InboxFilterDropdown";
import IconButtonWithTooltip from "./Shared/IconButtonWithTooltip";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserDetailsData,
  selectInboxInfoLabels,
  selectInboxInfoLoading,
  selectInboxRecentData,
  selectInboxRecentLoading,
  selectInboxRecentInitialized,
  selectInboxRecentPagination,
  selectInboxRecentIsLoadingMore,
  selectInboxRecentLazyTabsLoaded,
  selectInboxRecentSearchResults,
  selectInboxRecentIsSearching,
  selectSelectedInboxTab,
  selectLiveCallsCount,
  selectLabelPermissions,
  selectContactPermissions,
  selectTeamMembersFromInbox,
  selectTeamMembersWithNames,
  selectLifecycleByStage,
  selectAiAgents,
  selectAgentByAgentId,
} from "../../redux/selectors";
import { fetchUserDetails } from "../../redux/Slice/User/userDetailsSlice";
import { fetchLabels, fetchGroupById } from "../../redux/Slice/Inbox/inboxInfoSlice";
import { fetchAiAgents } from "../../redux/Slice/AiAgents/aiAgentsSlice";
import { addContact } from "../../redux/Slice/Contacts/contactsSlice";
import useContactChannel from "../../features/whatsapp/hooks/useContactChannel";
import ContactChannelPicker from "../Contacts/Features/ContactChannelPicker";
import { useContactSelection } from "../../hooks/useContactSelection";
import { fetchInbox, loadMoreInbox, fetchClosedTabInbox, searchInbox, clearSearchResults, removeGroupFromInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import { useNavigate, useParams } from "react-router-dom";
import { setCurrentChat, fetchTeamMemberChatMessages, clearCurrentChat, fetchTeamMemberChats } from "../../redux/Slice/Inbox/teamMemberChatSlice";
import { clearContactInfo } from "../../redux/Slice/Inbox/inboxInfoSlice";
import { setselectedContactID } from "../../redux/Slice/Inbox/inboxAllSlice";
import { clearSkipNextTabChangeClear } from "../../redux/Slice/Inbox/inboxSlice";
import { updateWhatsappQrState, updateWhatsappQrConnection, updateWhatsappQrConnectionLimits, updateWhatsappQrBurstPause, updateWhatsappQrDailyLimitReached } from "../../redux/Slice/Workspace/workspaceSlice";
import { stringAvatar, stringToColor } from "../../config/AvatarColor";
import { formatMessageTime } from "../../utils/timestampUtils";
import { getFromLocalStorage, setToLocalStorage } from "../../utils/localStorage";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from "libphonenumber-js";
import ConnectionBanner from "../ConnectionBlocker/ConnectionBanner";
import {
  getConnectionStatus,
  retryConnection,
  onConnectionChange,
  onDisconnectionChange,
  onReconnectingChange,
  onReconnectFailedChange,
} from "../../utils/socketHandler";
import connectionMonitor from "../../utils/connectionMonitor";
import {
  dispatchInboxSidebarTabChangeClear,
  navigateOffCustomerChatRoute,
} from "../../utils/inboxTabSwitch";
import {
  inboxContactMatchesUnreadFilter,
  inboxContactMatchesUnrepliedFilter,
} from "../../utils/inboxThreadFlags";
import { getTeamMemberMessagePreviewLabel } from "../../utils/teamMemberChatPreview";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";
import { slimScrollbarSx } from "../../utils/scrollbarStyles";
import InboxBulkActionBar from "./Features/InboxBulkActionBar";
import InboxBulkJobTracker from "./Features/InboxBulkJobTracker";
import {
  selectBulkSelectionActive,
  enterBulkSelectionMode,
  selectActiveBulkJob,
  isBulkJobRunning,
} from "../../redux/Slice/Inbox/inboxBulkSelectionSlice";

function teamMemberBelongsToWorkspace(member, workspaceId) {
  if (!workspaceId) return true;
  const ids = member.workspaceIds;
  if (!ids || !Array.isArray(ids) || ids.length === 0) return true;
  const ws = String(workspaceId);
  return ids.some((id) => String(id) === ws);
}

function formatTeamMemberLastMessagePreview(lastMessage) {
  return getTeamMemberMessagePreviewLabel(lastMessage);
}

const EMPTY_ARRAY = [];

const OFFLINE_STATUS = {
  isConnected: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 2,
};

// Chats' Show/Sort/Unreplied filter state, persisted across refreshes and
// logins (same browser) — deliberately not the search term, which is
// ephemeral, not a "preference" to remember.
const CHATS_FILTER_STORAGE_KEY = "inbox_chats_filters";

// *** Search state for message card as props to message card ***
const InboxAll = ({ onMenuClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid, userid, contactid } = useParams();
  const { selectContact } = useContactSelection();

  // Use memoized selectors to prevent unnecessary re-renders
  const userDetails = useSelector(selectUserDetailsData);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const labels = useSelector(selectInboxInfoLabels);
  const isLoading = useSelector(selectInboxInfoLoading);

  // Add permission selectors
  const LabelPermissions = useSelector(selectLabelPermissions);
  const ContactPermissions = useSelector(selectContactPermissions);

  const [searchTerm, setSearchTerm] = useState("");
  // The term search actually acts on -- only set on Enter (handleSearchSubmit
  // below), never per keystroke. searchTerm above still drives the input
  // box's own live-typed display value; every filtering/list-mode decision
  // reads this one instead (2026-09-08, "no per alphabet searching").
  const [committedSearchTerm, setCommittedSearchTerm] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createContactOpen, setCreateContactOpen] = useState(false);
  // Which WhatsApp the new contact is added on (official Meta number vs a QR number). Nothing to choose -- and nothing shown
  // -- for a workspace with only the Meta number; same rules and picker as Contacts -> Add contact.
  const contactChannel = useContactChannel(createContactOpen);
  // Restored once on mount from localStorage — see the persistence effect
  // below, which writes back whenever any of the three change.
  const [activeFilter, setActiveFilter] = useState(
    () => getFromLocalStorage(CHATS_FILTER_STORAGE_KEY, {})?.activeFilter ?? null
  ); // 'opened', 'unread', 'whatsapp', 'facebook', 'instagram', 'website'
  // Independent of activeFilter — combines with whichever Show value is
  // active (e.g. "Open" + "Unreplied" together), rather than being one more
  // mutually-exclusive branch of it. See InboxFilterDropdown's Show/Sort split.
  const [unrepliedOnly, setUnrepliedOnly] = useState(
    () => getFromLocalStorage(CHATS_FILTER_STORAGE_KEY, {})?.unrepliedOnly ?? false
  );
  const [sortOrder, setSortOrder] = useState(
    () => getFromLocalStorage(CHATS_FILTER_STORAGE_KEY, {})?.sortOrder ?? "newest"
  ); // 'newest' | 'oldest'
  // Shared between Chats and Calls — both sections' search now lives in this
  // one header row instead of Calls having its own separate search further
  // down the tree (see CallsSectionBody's searchValue/onSearchChange props).
  const [searchOpen, setSearchOpen] = useState(false);
  const [callsSearchTerm, setCallsSearchTerm] = useState("");
  const [inboxSection, setInboxSection] = useState("chats"); // 'chats' | 'calls'
  const liveCallsCount = useSelector(selectLiveCallsCount);
  const userPermissions = useSelector((state) => state.userDetails.userPermissions);
  // Same fail-open-while-loading pattern used everywhere else this permission
  // is checked (LeftNav's Calls nav item, CallButton, CallsSectionBody).
  // Calling is a permanent, by-design QR limitation (see LeftNav.jsx's callsNavAllowed,
  // same reasoning) — currentWorkspace is already selected above.
  const isQrConnectedWorkspace = currentWorkspace?.whatsappConnectionType === "baileys";
  const callsViewAllowed = (userPermissions == null || userPermissions?.permissions?.Calls?.View === true) && !isQrConnectedWorkspace;

  useEffect(() => {
    setToLocalStorage(CHATS_FILTER_STORAGE_KEY, { activeFilter, unrepliedOnly, sortOrder });
  }, [activeFilter, unrepliedOnly, sortOrder]);

  // Switching sections while search is open would otherwise leave a stale
  // open search box pointed at the section you just left. Also fires when a
  // contact is opened from a search result (see the contactid effect below)
  // -- closing the search box without clearing the term left the list stuck
  // showing only the searched-for row(s), with no visible search box open to
  // explain why (2026-09-08 bug fix). Clearing both search terms here (not
  // just whichever section is active) keeps this one place fully resetting
  // search state whenever it visibly closes, same as the "X"/Escape close.
  const handleSectionChange = (nextSection) => {
    setInboxSection(nextSection);
    setSearchOpen(false);
    setSearchTerm("");
    setCallsSearchTerm("");
  };

  // Permission was revoked (or this agent never had it) after "calls" was
  // already selected — fall back to Chats rather than leave the section
  // switched to a tab that's about to disappear out from under them.
  useEffect(() => {
    if (!callsViewAllowed && inboxSection === "calls") handleSectionChange("chats");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callsViewAllowed, inboxSection]);

  // Whenever the URL opens a specific contact's chat — accepting a call,
  // clicking any call card (live or historical), or plain contact-list
  // navigation — the sidebar must coherently switch back to Chats so that
  // conversation is actually visible, instead of silently staying on the
  // Calls list while the chat opens behind it. Only fires on a genuine
  // navigation (contactid changing), never on a manual section toggle.
  //
  // BUG FIX (Rahim, 2026-09-18): this used to call handleSectionChange
  // unconditionally, which also wipes the search box/results (that's
  // handleSectionChange's whole job — see its own comment above). That's
  // correct when actually leaving Calls for Chats, but this effect fires on
  // EVERY contactid change, including opening a chat from a search result
  // you were already viewing inside Chats — no section was left, so there's
  // nothing to clean up, yet it silently killed your own search. Only run
  // the section-reset when a section switch is actually happening.
  useEffect(() => {
    if (contactid && inboxSection !== "chats") handleSectionChange("chats");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactid]);

  // Dropdown state
  const [, ] = useState(null);

  // Add these new states
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [phoneError, setPhoneError] = useState("");
  const { guard } = useSubscriptionGuard();

  // Connection status state
  const [connectionStatus, setConnectionStatus] = useState(OFFLINE_STATUS);

  // Get inbox data and selected tab from Redux — all via memoized selectors
  const inboxData      = useSelector(selectInboxRecentData);
  const selectedInboxTab = useSelector(selectSelectedInboxTab);
  // WhatsApp (QR) groups need Groups.View (Owner always). The server already sends no group rows or live group events to
  // a role without it; this also drops any group card already in the list when the permission is removed live.
  const canViewGroups = useSelector(
    (state) =>
      state.userDetails?.userDetails?.roleName === "Owner" ||
      state.userDetails?.userPermissions?.permissions?.Groups?.View === true
  );
  const inboxPagination = useSelector(selectInboxRecentPagination);
  const isLoadingMore  = useSelector(selectInboxRecentIsLoadingMore);
  const isLoadingInbox = useSelector(selectInboxRecentLoading);
  const inboxInitialized = useSelector(selectInboxRecentInitialized);
  const lazyTabsLoaded = useSelector(selectInboxRecentLazyTabsLoaded);
  const searchResults  = useSelector(selectInboxRecentSearchResults);
  const isSearching    = useSelector(selectInboxRecentIsSearching);
  const lifecycleByStage = useSelector(selectLifecycleByStage);
  const agentByAgentId = useSelector(selectAgentByAgentId);

  // Get team member chat data
  const teamMemberChatsRaw = useSelector((state) => state.teamMemberChat?.chats);
  const teamMemberChats = teamMemberChatsRaw ?? EMPTY_ARRAY;
  const currentTeamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const currentUserId = useSelector((state) => state.userDetails?.userDetails?._id);
  
  // Get all team members (not just those with chats)
  const allTeamMembersRaw = useSelector(selectTeamMembersFromInbox);
  const allTeamMembers = allTeamMembersRaw ?? EMPTY_ARRAY;
  const teamMembersWithNamesRaw = useSelector(selectTeamMembersWithNames);
  const teamMembersWithNames = teamMembersWithNamesRaw ?? EMPTY_ARRAY;

  // Current workspace's AI agents — same roster role as allTeamMembers/
  // teamMembersWithNames above, just backed by aiAgentsSlice (see aiAgentsSlice.js).
  const aiAgentsRaw = useSelector(selectAiAgents);
  // eslint-disable-next-line no-unused-vars
  const aiAgents = aiAgentsRaw ?? EMPTY_ARRAY;

  // Check if "Chat with Members" tab is selected
  const isChatWithMembersTab = selectedInboxTab === "Chat with Members";
  // Inbox multi-select: while any conversation is checked (or selection mode was entered from
  // the touch-only "Select" chip), the bulk action bar takes the filter row's place. Not offered
  // on Chat with Members (team chats, not conversations) or Blocked Contacts.
  const bulkSelectionActive = useSelector(selectBulkSelectionActive);
  const bulkSelectionAvailable = !isChatWithMembersTab && selectedInboxTab !== "Blocked Contacts";
  const isTouchOnly = useMediaQuery("(hover: none)");
  // While a bulk job the user started is running (and not hidden), its progress strip takes the
  // filter row's place instead — unless they're already selecting again.
  const activeBulkJob = useSelector(selectActiveBulkJob);
  const bulkSelectingNow = bulkSelectionAvailable && bulkSelectionActive;
  const bulkJobStripVisible = !bulkSelectingNow && isBulkJobRunning(activeBulkJob) && !activeBulkJob.dismissed;

  // Stable filter function — only recreated when activeFilter changes
  const filterContacts = useCallback((contacts) => {
    if (!activeFilter || !contacts || contacts.length === 0) {
      return contacts;
    }

    switch (activeFilter) {
      case 'opened':
        return contacts.filter(contact => contact.activeStatus !== 0);
      case 'unread':
        return contacts.filter((contact) => inboxContactMatchesUnreadFilter(contact));
      case 'whatsapp':
        return contacts.filter(contact => contact.platform === 'whatsapp');
      case 'qr':
        return contacts.filter(contact => contact.platform === 'qr');
      case 'facebook':
        return contacts.filter(contact => contact.platform === 'facebook');
      case 'instagram':
        return contacts.filter(contact => contact.platform === 'instagram');
      case 'website':
        return contacts.filter(contact => contact.platform === 'website');
      default:
        return contacts;
    }
  }, [activeFilter]);

  // Memoize the full contacts-to-show computation so MessageCard only re-renders
  // when the data it actually cares about changes (not on every socket dispatch).
  const isSearchMode = Boolean(committedSearchTerm && !isChatWithMembersTab);
  const contactsToShow = useMemo(() => {
    let base = [];
    if (selectedInboxTab === "All") {
      base = inboxData.All || [];
    } else if (selectedInboxTab === "You") {
      base = inboxData.You || [];
    } else if (selectedInboxTab === "Active") {
      base = inboxData.Active || [];
    } else if (selectedInboxTab === "Unassigned") {
      base = inboxData.Unassigned || [];
    } else if (selectedInboxTab === "Assigned") {
      base = inboxData.Assigned || [];
    } else if (selectedInboxTab === "Closed") {
      base = inboxData.Closed || [];
    } else if (selectedInboxTab === "Blocked Contacts") {
      base = inboxData.Blocked || [];
    } else if (selectedInboxTab === "Groups") {
      base = inboxData.Groups || [];
    } else if (inboxData.TeamMembers?.some((tm) => tm._id === selectedInboxTab)) {
      base = inboxData.TeamMembers.find((tm) => tm._id === selectedInboxTab)?.data || [];
    } else if (inboxData.Teams?.some((t) => t._id === selectedInboxTab)) {
      base = inboxData.Teams.find((t) => t._id === selectedInboxTab)?.data || [];
    } else if (typeof selectedInboxTab === "string" && selectedInboxTab.startsWith("lifecycle:")) {
      // A Lifecycle stage was clicked in InboxSettings.jsx's rail — selectedInboxTab is
      // `lifecycle:${stageId}` (prefixed so it can't collide with a real TeamMember/Team
      // id above). Each stage has its OWN cached bucket in lifecycleByStage (session-cached,
      // kept live by socketHandler.js) — unlike Closed/Blocked's single shared array, this
      // must key off the specific stageId, not a fixed key.
      const stageId = selectedInboxTab.slice("lifecycle:".length);
      base = lifecycleByStage[stageId]?.data || [];
    } else if (typeof selectedInboxTab === "string" && selectedInboxTab.startsWith("agent:")) {
      // An AI agent was clicked in InboxSettings.jsx's rail — same "own cached bucket,
      // keyed by id" pattern as Lifecycle just above, see agentByAgentId/fetchAgentTabInbox.
      const agentId = selectedInboxTab.slice("agent:".length);
      base = agentByAgentId[agentId]?.data || [];
    }

    // Apply active (Show) filter
    let filtered = filterContacts(base);

    // Unreplied is an independent toggle, not a branch of activeFilter — see
    // InboxFilterDropdown's Show/Sort split — so it combines with whatever
    // Show value is active instead of replacing it.
    if (unrepliedOnly) {
      filtered = filtered.filter((contact) => inboxContactMatchesUnrepliedFilter(contact));
    }

    // Strip error sentinel objects
    filtered = filtered.filter(contact => !(contact?.error && contact?.details));

    if (!canViewGroups) filtered = filtered.filter((contact) => !contact?.isGroup);

    // Search mode: the DB result only, once it actually arrives -- no local
    // pre-population (2026-09-08, Rahim: "skip the local search... wait for
    // the DB call to finish"). searchResults is null until the real fetch
    // resolves, so this deliberately renders nothing (paired with the
    // loading state in MessageCard) rather than a one-then-two-then-three
    // flicker of local matches getting replaced by the real set. Sort order
    // doesn't apply here, same as any search (results are relevance-ordered).
    if (isSearchMode) {
      return searchResults || [];
    }

    // "Newest" is the data's existing arrival order (no-op); "Oldest" just
    // reverses it — no new timestamp plumbing needed.
    if (sortOrder === "oldest") {
      filtered = filtered.slice().reverse();
    }

    return filtered;
  }, [
    selectedInboxTab,
    inboxData,
    lifecycleByStage,
    agentByAgentId,
    unrepliedOnly,
    sortOrder,
    filterContacts,
    isSearchMode,
    searchResults,
    canViewGroups,
  ]);

  // Derive pagination state for the current tab. "Blocked Contacts" is the tab label
  // (matches InboxSettings/contactsToShow branching above); "Blocked" is the backend
  // category key (matches inboxRecent.pagination / inboxController.js) — translate here
  // so load-more pagination works the same way it does for every same-named tab.
  // Lifecycle/Agent read from their own per-id cache (lifecycleByStage/agentByAgentId)
  // instead of the shared inboxPagination object — each stage/agent has its own
  // hasMore/page, not one fixed "Lifecycle"/"Agent" key.
  const isLifecycleTab = typeof selectedInboxTab === "string" && selectedInboxTab.startsWith("lifecycle:");
  const isAgentTab = typeof selectedInboxTab === "string" && selectedInboxTab.startsWith("agent:");
  const paginationCategory = selectedInboxTab === "Blocked Contacts" ? "Blocked" : selectedInboxTab;
  const currentTabPagination = isLifecycleTab
    ? (lifecycleByStage[selectedInboxTab.slice("lifecycle:".length)] || { hasMore: false })
    : isAgentTab
    ? (agentByAgentId[selectedInboxTab.slice("agent:".length)] || { hasMore: false })
    : (inboxPagination[paginationCategory] || { hasMore: false });
  const currentTabHasMore = currentTabPagination.hasMore || false;

  const MAX_AUTO_SCROLL = 300;

  // Auto-scroll: only fires below the cap (prevents loading all contacts via scroll)
  const handleLoadMore = useCallback(() => {
    if (currentTabHasMore && !isLoadingMore && contactsToShow.length < MAX_AUTO_SCROLL) {
      dispatch(loadMoreInbox(paginationCategory));
    }
  }, [dispatch, paginationCategory, currentTabHasMore, isLoadingMore, contactsToShow.length]);

  // Explicit "More" button: user-initiated, always loads next 50 regardless of cap
  const handleExplicitLoadMore = useCallback(() => {
    if (currentTabHasMore && !isLoadingMore) {
      dispatch(loadMoreInbox(paginationCategory));
    }
  }, [dispatch, paginationCategory, currentTabHasMore, isLoadingMore]);

  const showLoadMoreButton = !isChatWithMembersTab &&
    currentTabHasMore &&
    contactsToShow.length >= MAX_AUTO_SCROLL;

  // Dispatch fetchUserDetails on component mount - only once
  useEffect(() => {
    if (!userDetails) {
      dispatch(fetchUserDetails());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userDetails intentionally excluded to prevent loops
  }, [dispatch]);

  // Fetch labels when component mounts
  useEffect(() => {
    if (LabelPermissions.View) {
      dispatch(fetchLabels());
    }
  }, [dispatch, LabelPermissions.View]);

  // Fetch team member chats once on mount so sidebar "Chat with Members" count
  // is accurate even before opening that tab. Chats are account-scoped (not
  // per-workspace), so switching workspaces doesn't need a refetch.
  useEffect(() => {
    dispatch(fetchTeamMemberChats());
  }, [dispatch]);

  // Fetch the current workspace's AI agents — unlike team member chats above,
  // agents ARE workspace-scoped (AgentModel.workspaceId), so this refetches
  // whenever the active workspace changes, not just once on mount.
  useEffect(() => {
    if (currentWorkspace?._id) {
      dispatch(fetchAiAgents({ workspaceId: currentWorkspace._id }));
    }
  }, [dispatch, currentWorkspace?._id]);

  // Clear team member chat when switching away from "Chat with Members" tab
  useEffect(() => {
    // If we're not on the "Chat with Members" tab and a team member chat is open, clear it
    if (selectedInboxTab !== "Chat with Members" && currentTeamMemberChat?.otherMemberId) {
      dispatch(clearCurrentChat());
    }
  }, [selectedInboxTab, currentTeamMemberChat?.otherMemberId, dispatch]);

  // Lazy-load the Closed/Blocked tabs' own data on first click (see fetchClosedTabInbox's
  // onlyCategory fast path). InboxSettings.jsx's handleSettingClick already fires this on
  // the click itself, but this effect is the fallback for whenever the tab is already
  // selected when InboxAll (re)mounts — e.g. a page refresh restoring the tab — so the
  // Blocked Contacts case needs the same coverage Closed already has, or the list stays
  // permanently empty while the sidebar badge keeps showing the real count.
  useEffect(() => {
    if (selectedInboxTab === "Closed" && !lazyTabsLoaded.Closed) {
      dispatch(fetchClosedTabInbox("Closed"));
    } else if (selectedInboxTab === "Blocked Contacts" && !lazyTabsLoaded.Blocked) {
      dispatch(fetchClosedTabInbox("Blocked"));
    } else if (selectedInboxTab === "Groups" && !lazyTabsLoaded.Groups) {
      dispatch(fetchClosedTabInbox("Groups"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the Closed/Blocked/Groups flags should retrigger this, not the whole lazyTabsLoaded object
  }, [selectedInboxTab, lazyTabsLoaded.Closed, lazyTabsLoaded.Blocked, lazyTabsLoaded.Groups, dispatch]);

  // Search fires on Enter only, not per-keystroke (2026-09-08 change).
  // Nothing reacts to typing at all now -- committing the term is what
  // switches the list into search mode in the first place (isSearchMode
  // above), not a live debounce. handleSearchSubmit is wired to
  // InboxSearchToggle's onSubmit (Enter key), Chats section only -- Calls
  // search is local-only, no backend call to trigger.
  const handleSearchSubmit = () => {
    setCommittedSearchTerm(searchTerm);
    // Chat with Members' own search (below) is entirely local -- filters
    // teamMemberChatsList client-side, unrelated to contacts -- so Enter
    // there should only commit the term for that local filter, never fire
    // a real contact-search API call.
    if (isChatWithMembersTab) return;
    if (searchTerm.trim()) {
      // Groups tab: search groups by name; every other tab: contacts only (the server leaves groups out).
      dispatch(searchInbox(selectedInboxTab === "Groups" ? { term: searchTerm.trim(), category: "Groups" } : searchTerm.trim()));
    } else {
      dispatch(clearSearchResults());
    }
  };
  // Clearing the box (X/Escape, or the auto-clear on selecting a search
  // result) still needs to drop any stale server results out of redux --
  // cheap local cleanup, not a network call -- and also needs to drop the
  // committed term so a closed search box doesn't leave the list stuck.
  useEffect(() => {
    if (!searchTerm) {
      dispatch(clearSearchResults());
      setCommittedSearchTerm("");
    }
  }, [searchTerm, dispatch]);

  const prevInboxTabRef = useRef(null);
  const currentTeamMemberChatRef = useRef(currentTeamMemberChat);
  currentTeamMemberChatRef.current = currentTeamMemberChat;
  const skipNextTabChangeClear = useSelector((state) => state.inbox.skipNextTabChangeClear);
  // Leaving any inbox sidebar tab closes the open chat and normalizes the URL (layout effect: before paint, avoids flash/races with MessageCard URL sync)
  useLayoutEffect(() => {
    if (prevInboxTabRef.current === null) {
      prevInboxTabRef.current = selectedInboxTab;
      return;
    }
    if (prevInboxTabRef.current === selectedInboxTab) {
      return;
    }
    prevInboxTabRef.current = selectedInboxTab;
    // MessageCard.jsx sets this flag right before switching this tab itself, to reveal
    // a deep-linked Closed/Blocked contact — that's not a user leaving the tab, so the
    // open chat/URL must not be cleared for it. Consumed once, then reset.
    if (skipNextTabChangeClear) {
      dispatch(clearSkipNextTabChangeClear());
      return;
    }
    dispatchInboxSidebarTabChangeClear(dispatch);
    // Rahim, 2026-09-18: switching sidebar categories (All/Me/Active/Unassigned/Closed/...)
    // while a search is active should exit search, same as clicking the X/Escape --
    // otherwise contactsToShow's isSearchMode override (see above) keeps showing the old
    // search results under the newly-picked tab instead of that tab's own list. This branch
    // already only runs for a genuine user-driven switch (the skipNextTabChangeClear check
    // above filters out MessageCard's own deep-link auto-correction), so it can't re-trigger
    // the "opening a chat from search" bug the previous fix addressed.
    setSearchOpen(false);
    setSearchTerm("");
    const effectiveUserid = userid || userDetails?._id;
    navigateOffCustomerChatRoute(navigate, accountid, effectiveUserid);
  }, [
    selectedInboxTab,
    skipNextTabChangeClear,
    dispatch,
    userid,
    userDetails?._id,
    accountid,
    navigate,
  ]);

  // Monitor connection status
  useEffect(() => {
    const updateConnectionStatus = () => {
      try {
        const status = getConnectionStatus();
        // Ensure we only set valid status objects
        if (status && typeof status === 'object' && !status.message) {
          setConnectionStatus(status);
        } else {
          setConnectionStatus(OFFLINE_STATUS);
        }
      } catch (error) {
        setConnectionStatus(OFFLINE_STATUS);
      }
    };

    // Check if we're offline
    const checkOfflineStatus = () => {
      if (!navigator.onLine) {
        setConnectionStatus(OFFLINE_STATUS);
        return;
      }
      updateConnectionStatus();
    };

    // Update immediately
    checkOfflineStatus();

    const refetchTeamMemberChatAfterConnection = () => {
      dispatch(fetchTeamMemberChats());
      const otherMemberId = currentTeamMemberChatRef.current?.otherMemberId;
      if (otherMemberId) {
        dispatch(
          fetchTeamMemberChatMessages({ otherMemberId, limit: 50, offset: 0 })
        );
      }
    };

    // Listen for online/offline events
    const handleOnline = () => {
      // When online, refetch data
      dispatch(fetchInbox());
      dispatch(fetchUserDetails());
      if (LabelPermissions.View) {
        dispatch(fetchLabels());
      }
      refetchTeamMemberChatAfterConnection();
      updateConnectionStatus();
    };

    const handleOffline = () => {
      setConnectionStatus(OFFLINE_STATUS);
    };

    // Also listen for socket connection events
    const handleSocketConnection = () => {
      // Refetch data when socket connects
      dispatch(fetchInbox());
      dispatch(fetchUserDetails());
      if (LabelPermissions.View) {
        dispatch(fetchLabels());
      }
      refetchTeamMemberChatAfterConnection();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for socket connection events
    window.addEventListener('socketConnected', handleSocketConnection);

    // Direct subscription to the connection monitor's real events — this is
    // what actually keeps the banner's isConnected/isReconnecting/attempt
    // counter live and correct (the online/offline/socketConnected listeners
    // above only drive data refetching, not this component's own status
    // display, and previously left the banner stuck stale after a manual
    // retryConnection() succeeded).
    const handleConnected = () => {
      setConnectionStatus((prev) => ({ ...prev, isConnected: true, isReconnecting: false, reconnectAttempts: 0 }));
    };
    const handleDisconnected = () => {
      setConnectionStatus((prev) => ({ ...prev, isConnected: false, isReconnecting: true }));
    };
    const handleReconnecting = (data) => {
      setConnectionStatus((prev) => ({
        ...prev,
        isConnected: false,
        isReconnecting: true,
        reconnectAttempts: data?.reconnectAttempts ?? prev.reconnectAttempts,
        maxReconnectAttempts: data?.maxReconnectAttempts ?? prev.maxReconnectAttempts,
      }));
    };
    const handleReconnectFailed = (data) => {
      const status = getConnectionStatus();
      setConnectionStatus({
        isConnected: false,
        isReconnecting: false,
        reconnectAttempts: status.reconnectAttempts || (data?.reconnectAttempts ?? 0),
        maxReconnectAttempts: status.maxReconnectAttempts || (data?.maxReconnectAttempts ?? 2),
      });
    };

    onConnectionChange(handleConnected);
    onDisconnectionChange(handleDisconnected);
    onReconnectingChange(handleReconnecting);
    onReconnectFailedChange(handleReconnectFailed);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('socketConnected', handleSocketConnection);
      connectionMonitor.off('connection', handleConnected);
      connectionMonitor.off('disconnection', handleDisconnected);
      connectionMonitor.off('reconnecting', handleReconnecting);
      connectionMonitor.off('reconnectFailed', handleReconnectFailed);
    };
  }, [dispatch, LabelPermissions.View]);

  // Real-time "burst-pause" notice for QR (Baileys) WhatsApp — fired by whatsapp-worker the
  // moment a connection hits its every-100-messages 5-minute pacing pause (see
  // baileysConnectionManager.js's processSendQueue). Without this, a send attempt made during
  // that window just silently hangs (or times out at 30s while the message actually goes out
  // minutes later) with no explanation. Scoped to whichever workspace's socket room this client
  // is already joined to, same as whatsapp_qr:code/status — no extra workspaceId filtering
  // needed here.
  useEffect(() => {
    const handleBurstPause = (payload) => {
      const resumeInSeconds = payload?.resumeAt ? Math.max(0, Math.ceil((payload.resumeAt - Date.now()) / 1000)) : null;
      const waitCopy = resumeInSeconds ? ` Try again in about ${Math.ceil(resumeInSeconds / 60)} minute${resumeInSeconds > 60 ? 's' : ''}.` : '';
      toast.error(
        `This WhatsApp number sent a lot of messages recently, so sending is paused briefly to protect it.${waitCopy}`,
        { id: 'whatsapp-qr-burst-pause', duration: 6000 }
      );
      // Feeds TextBox.jsx's own composer-hiding banner (2026-09-28 follow-up) -- purely
      // additive alongside the toast above, not a replacement for it.
      // Multi-number: the workspace-level composer state tracks the FIRST number (slot 0) only; an
      // additional number's pause still gets the toast above but must not hide this composer.
      if (!(payload?.slot > 0)) dispatch(updateWhatsappQrBurstPause(payload?.resumeAt || null));
      // Each number keeps its own rest state, so only the chats of THAT number show the banner.
      if (payload?.connectionId) {
        dispatch(updateWhatsappQrConnectionLimits({ connectionId: payload.connectionId, burstPauseUntil: payload.resumeAt || null, burstDraining: payload.draining }));
      }
    };
    const handleBurstResume = (payload) => {
      if (!(payload?.slot > 0)) dispatch(updateWhatsappQrBurstPause(null));
      if (payload?.connectionId) {
        dispatch(updateWhatsappQrConnectionLimits({ connectionId: payload.connectionId, burstPauseUntil: null, burstDraining: false }));
      }
    };
    // A scan the number rules refused (the worker logs that session out): say why, right where the owner is looking.
    const handleQrRejected = (payload) => {
      if (payload?.message) toast.error(payload.message, { id: 'whatsapp-qr-rejected', duration: 9000 });
    };
    connectionMonitor.on('whatsapp_qr:burst_pause', handleBurstPause);
    connectionMonitor.on('whatsapp_qr:burst_resume', handleBurstResume);
    connectionMonitor.on('whatsapp_qr:rejected', handleQrRejected);
    return () => {
      connectionMonitor.off('whatsapp_qr:burst_pause', handleBurstPause);
      connectionMonitor.off('whatsapp_qr:burst_resume', handleBurstResume);
      connectionMonitor.off('whatsapp_qr:rejected', handleQrRejected);
    };
  }, [dispatch]);

  // Real-time daily-send-cap notices for QR (Baileys) WhatsApp — fired at 80%/90%/100% of a
  // connection's daily limit (checkAndReportDailyLimit in baileysConnectionManager.js). This
  // event existed since Phase 2 but had no frontend listener at all until now, same gap
  // whatsapp_qr:burst_pause had before its own fix. Same toast id as the synchronous
  // BAILEYS_DAILY_LIMIT_EXCEEDED rejection toast (InboxMessageSlice.js's notifyIfBaileysBlocked)
  // so a graduated warning and a later hard rejection coalesce into one updating toast instead
  // of stacking.
  useEffect(() => {
    const handleDailyLimitAlert = (payload) => {
      const { count, limit, percent, resetAt } = payload || {};
      if (percent >= 100) {
        toast.error(
          `This WhatsApp number has reached its daily send limit (${count}/${limit}) — further sends are blocked until the next day.`,
          { id: 'whatsapp-qr-daily-limit', duration: 6000 }
        );
      } else {
        toast(
          `This WhatsApp number is approaching its daily send limit (${count}/${limit}, ${percent}%).`,
          { id: 'whatsapp-qr-daily-limit', duration: 5000, icon: '⚠️' }
        );
      }
      // Feeds TextBox.jsx's own composer-hiding banner (2026-09-28 follow-up) -- purely
      // additive alongside the toasts above, not a replacement for them. Only the 100% case
      // hides the composer; 80%/90% stay warning-only (the toast branch above), same
      // graduated severity the toasts themselves already use.
      if (!(payload?.slot > 0)) dispatch(updateWhatsappQrDailyLimitReached({ reached: percent >= 100, resetAt }));
      if (payload?.connectionId) dispatch(updateWhatsappQrConnectionLimits({ connectionId: payload.connectionId, dailyLimitReached: percent >= 100 }));
    };
    connectionMonitor.on('whatsapp_qr:daily_limit_alert', handleDailyLimitAlert);
    return () => {
      connectionMonitor.off('whatsapp_qr:daily_limit_alert', handleDailyLimitAlert);
    };
  }, [dispatch]);

  // Group info live refresh: a member added/removed (or a setting changed) from a PHONE reaches the
  // server as a WhatsApp event, and the server pushes `group_updated` once the stored group record
  // is current. Re-fetch only if that group's info box is the one currently loaded.
  const openGroupId = useSelector((state) => state.inboxInfo?.groupInfo?.groupID);
  useEffect(() => {
    if (!openGroupId) return undefined;
    const handleGroupUpdated = (payload) => {
      if (payload?.groupId && String(payload.groupId) === String(openGroupId)) {
        dispatch(fetchGroupById(openGroupId));
      }
    };
    connectionMonitor.on('group_updated', handleGroupUpdated);
    return () => {
      connectionMonitor.off('group_updated', handleGroupUpdated);
    };
  }, [dispatch, openGroupId]);

  // We left / were removed from a group (from the phone, or by a teammate in the web app): drop it
  // from the lists and, if it is the open chat, step back to the inbox home.
  useEffect(() => {
    const handleGroupLeft = (payload) => {
      const groupId = payload?.groupId;
      if (!groupId) return;
      dispatch(removeGroupFromInbox(groupId));
      if (String(contactid || "") === String(groupId)) {
        dispatch(setselectedContactID(null));
        navigateOffCustomerChatRoute(navigate, accountid, userid || userDetails?._id);
      }
    };
    connectionMonitor.on('group_left', handleGroupLeft);
    return () => {
      connectionMonitor.off('group_left', handleGroupLeft);
    };
  }, [dispatch, navigate, accountid, userid, contactid, userDetails?._id]);

  // Live QR (Baileys) connection-state updates (2026-09-26 follow-up) -- same event whatsapp-worker
  // already emits on every connection state transition (used since Phase 2 for the QR-scan flow's
  // own status polling), just not previously fed into currentWorkspace so the Inbox composer could
  // react to a disconnect/logout without a manual refresh. See TextBox.jsx's own isQrDisconnected
  // for what reads this.
  useEffect(() => {
    const handleQrStatus = (payload) => {
      if (payload?.state) {
        // Multi-number: slot 0 (or an event without a slot, i.e. every single-number workspace)
        // drives the existing workspace-level state exactly as before; additional numbers only
        // patch their own entry in whatsappQrConnections.
        if (!(payload.slot > 0)) dispatch(updateWhatsappQrState(payload.state));
        if (payload.connectionId) dispatch(updateWhatsappQrConnection(payload));
      }
    };
    connectionMonitor.on('whatsapp_qr:status', handleQrStatus);
    return () => {
      connectionMonitor.off('whatsapp_qr:status', handleQrStatus);
    };
  }, [dispatch]);

  // Handle retry connection — retry the socket connection in place, no page reload
  const handleRetryConnection = () => {
    retryConnection();
  };



  // Handle modal toggles
  const handleFilterOpen = () => {
    if (LabelPermissions.View) {
      setFilterOpen(true);
    }
  };

  const handleFilterClose = () => setFilterOpen(false);

  const handleCreateContactOpen = async () => {
    if (!await guard(['contacts'])) return;
    setCreateContactOpen(true);
  };
  const handleCreateContactClose = () => {
    setName("");
    setPhone("");
    setCreateContactOpen(false);
  };

  // Handle label toggle in filter modal
  const handleLabelToggle = (label) => {
    setSelectedLabels((prev) => {
      if (prev.includes(label)) {
        return prev.filter((l) => l !== label);
      } else {
        return [...prev, label];
      }
    });
  };

  // Apply selected filters
  const applyLabelFilter = () => {
    // Close modal after applying filters
    handleFilterClose();
  };

  // Clear all selected labels
  const clearLabelFilter = () => {
    setSelectedLabels([]);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle team member chat click
  const handleTeamMemberChatClick = React.useCallback(async (chat) => {
    // Clear user chat selection first
    dispatch(setselectedContactID(null));
    
    // Clear contactInfo from Redux to prevent ChatInfo from using stale data
    dispatch(clearContactInfo());
    
    // Ensure we have a valid otherMemberId (convert to string if needed)
    const otherMemberId = String(chat.otherMemberId || chat.id?.replace('new_', '') || '');
    
    if (!otherMemberId || otherMemberId === 'undefined' || otherMemberId === 'null') {
      console.error("Invalid otherMemberId in chat:", chat);
      return;
    }
    
    // Open team member chat - set state immediately so ChatBox renders
    dispatch(setCurrentChat({
      otherMemberId: otherMemberId,
      otherMember: {
        id: otherMemberId,
        name: chat.name || "Team Member",
        email: chat.email || ''
      },
      keepMessages: false
    }));
    
    // Fetch messages for the team member chat (async, but don't block UI)
    dispatch(fetchTeamMemberChatMessages({ otherMemberId: otherMemberId })).catch(error => {
      console.error("Error fetching team member chat messages:", error);
    });
  }, [dispatch]);

  // Transform team member chats for display - show ALL team members
  const teamMemberChatsList = React.useMemo(() => {
    if (!isChatWithMembersTab) {
      return [];
    }
    
    // Create a map of existing chats by otherMemberId (normalize to string for consistent matching)
    const chatsMap = new Map();
    (teamMemberChats || []).forEach(chat => {
      const key = String(chat.otherMemberId || '');
      if (key && key !== 'undefined' && key !== 'null') {
        chatsMap.set(key, chat);
      }
    });
    
    // Create a map of team member names
    const memberNameMap = new Map();
    teamMembersWithNames.forEach(member => {
      const id = member._id || member.id;
      if (id) {
        memberNameMap.set(String(id), member.name || "Team Member");
      }
    });
    
    // Get team members in the current workspace only (empty workspaceIds = all workspaces)
    const availableMembers = allTeamMembers
      .filter((member) => teamMemberBelongsToWorkspace(member, currentWorkspace?._id))
      .filter(member => {
        const memberId = String(member._id || member.id || '');
        return memberId && memberId !== 'undefined' && memberId !== 'null' && memberId !== String(currentUserId);
      })
      .map(member => {
        const memberId = String(member._id || member.id || '');
        if (!memberId || memberId === 'undefined' || memberId === 'null') {
          return null; // Skip invalid members
        }
        // Use string key to match chatsMap
        const existingChat = chatsMap.get(String(memberId));
        const memberName = memberNameMap.get(memberId) || member.name || "Team Member";
        
        if (existingChat) {
          // Has existing chat - show last message
          const unreadCount = existingChat.unreadCount || 0;
          const lastMessage = existingChat.lastMessage;
          
          return {
            id: existingChat.chatId || existingChat._id,
            otherMemberId: String(memberId), // Ensure it's a string
            name: memberName,
            email: existingChat.otherMemberEmail || member.email || '',
            avatarColor: stringToColor(memberName),
            lastMessage: formatTeamMemberLastMessagePreview(lastMessage),
            timestamp: lastMessage?.timestamp || existingChat.updatedAt,
            unreadCount: unreadCount,
            messageType: lastMessage?.messageType || "text",
            hasChat: true,
          };
        } else {
          // No existing chat - show as available to start
          return {
            id: `new_${memberId}`,
            otherMemberId: String(memberId), // Ensure it's a string
            name: memberName,
            email: member.email || '',
            avatarColor: stringToColor(memberName),
            lastMessage: "Start conversation",
            timestamp: null,
            unreadCount: 0,
            messageType: "text",
            hasChat: false,
          };
        }
      })
      .sort((a, b) => {
        // Sort: chats with messages first, then by timestamp
        if (a.hasChat && !b.hasChat) return -1;
        if (!a.hasChat && b.hasChat) return 1;
        if (a.hasChat && b.hasChat) {
          const timeA = new Date(a.timestamp || 0);
          const timeB = new Date(b.timestamp || 0);
          return timeB - timeA;
        }
        // Both new - sort alphabetically
        return a.name.localeCompare(b.name);
      })
      .filter(member => member !== null); // Remove any null entries
    
    return availableMembers;
  }, [
    isChatWithMembersTab,
    teamMemberChats,
    allTeamMembers,
    teamMembersWithNames,
    currentUserId,
    currentWorkspace?._id,
  ]);

  // Filter team member chats by search term and separate into active chats and members without chats
  const { activeTeamMemberChats, teamMembersWithoutChats } = React.useMemo(() => {
    if (!isChatWithMembersTab) {
      return { activeTeamMemberChats: [], teamMembersWithoutChats: [] };
    }
    
    let filtered = teamMemberChatsList;
    
    // Apply search filter if a term has been committed (Enter) -- not the
    // raw per-keystroke searchTerm.
    if (committedSearchTerm) {
    const searchLower = committedSearchTerm.toLowerCase();
      filtered = teamMemberChatsList.filter(chat =>
      chat.name.toLowerCase().includes(searchLower) ||
      chat.lastMessage.toLowerCase().includes(searchLower)
    );
    }

    // Separate into active chats and members without chats
    const active = filtered.filter(chat => chat.hasChat);
    const withoutChats = filtered.filter(chat => !chat.hasChat);

    return { activeTeamMemberChats: active, teamMembersWithoutChats: withoutChats };
  }, [teamMemberChatsList, committedSearchTerm, isChatWithMembersTab]);

  // Handle contact creation and chat navigation
  const validateAndNormalizePhone = (raw) => {
    if (!raw || typeof raw !== "string")
      return { ok: false, message: "Phone is required" };
    const cleaned = raw.replace(/[\s\-()]/g, "").trim();
    const candidate = cleaned.startsWith("+")
      ? cleaned
      : cleaned.startsWith("00")
      ? "+" + cleaned.slice(2)
      : cleaned.match(/^\d+$/)
      ? "+" + cleaned
      : cleaned;
    if (!isValidPhoneNumber(candidate)) {
      return {
        ok: false,
        message: "Enter a valid phone number in international format",
      };
    }
    const parsed = parsePhoneNumberFromString(candidate);
    if (!parsed || !parsed.isValid()) {
      return { ok: false, message: "Enter a valid phone number" };
    }
    const digitsNoPlus = parsed.number.replace(/^\+/, "");
    return { ok: true, e164: parsed.number, digits: digitsNoPlus };
  };

  const handleCreateContact = async () => {
    if (!name || !phone) {
      setSnackbar({
        open: true,
        message: "Please fill in all fields",
        severity: "error",
      });
      return;
    }

    const phoneValidation = validateAndNormalizePhone(phone);
    if (!phoneValidation.ok) {
      setPhoneError(phoneValidation.message || "Invalid phone number");
      setSnackbar({
        open: true,
        message: phoneValidation.message || "Invalid phone number",
        severity: "error",
      });
      return;
    }

    try {
      // Create contact
      const result = await dispatch(
        addContact({
          name,
          phoneNumberID: phoneValidation.digits,
          ...contactChannel.target, // {} for the Meta way; { platform: "qr", connectionId } for a QR number
        })
      ).unwrap();

      // Get the new contact's ID
      const newContactId = result._id;

      // Set up chat view using unified contact selection
      await selectContact(newContactId, {
        navigate: true,
        accountid,
        userid,
        clearPrevious: true
      });

      // Refresh inbox data to show the new contact
      // Add small delay to ensure contact is saved on server
      setTimeout(() => {
        dispatch(fetchInbox());
      }, 500);

      // Close modal and show success message
      handleCreateContactClose();
      setSnackbar({
        open: true,
        message: "Contact created successfully",
        severity: "success",
      });

      // Navigation handled by selectContact hook
    } catch (error) {
      const rawMsg =
        typeof error === "string"
          ? error
          : error?.message || "Failed to create contact";
      const msg = /already exists/i.test(rawMsg)
        ? "Contact already exists"
        : rawMsg;
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  return (
    <Box
      sx={{
        padding: 0,
        margin: 0,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        position: "relative",
        backgroundColor: (theme) => theme.palette.background.default,
        flex: 1, // Use flex: 1 to properly fill flex container instead of height: "100%"
        minHeight: 0, // Important for flex child to shrink properly
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Prevent outer container from scrolling
        paddingBottom: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
        marginBottom: 0,
      }}
    >
      {/* Connection Banner */}
      <ConnectionBanner
        connectionStatus={connectionStatus}
        onRetry={handleRetryConnection}
        sx={{ margin: 0 }}
      />

      {/* Search and Filter Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // lg/xl top padding is 0 here (was 10px/12px) — that space now lives
          // inside the header row's own fixed height below, so the row's border-
          // bottom lands at the exact same 63px offset as InboxSettings.jsx's
          // heading divider, instead of an accumulated padding+content guess that
          // never actually landed on the same line. xs/sm/md unchanged — this
          // divider was already hidden there (see the old Divider's own
          // `display` below), so there's nothing to align at those sizes.
          padding: { xs: "5px 6px 3px 6px", sm: "4px 6px 3px 6px", md: "8px 12px 5px 12px", lg: "0px 14px 6px 14px", xl: "0px 16px 8px 16px" },
          gap: { xs: 0.5, sm: 0.5, md: 1, lg: 1.25, xl: 1.5 },
        }}
      >
        {/* Top row: the Chats/Calls switch, hamburger, and search all share
            one line — underlined text tabs (MUI Tabs — the actual component
            built for this, with a real animated TabIndicator, not a hand-
            rolled underline) in the app's own accent color, plus a shared
            search icon that works for both sections instead of Calls having
            its own separate search row further down the tree. */}
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            // Same 63px height InboxSettings.jsx's own heading box uses at lg/xl —
            // this row's border-bottom is now what actually lines up with it,
            // replacing the standalone Divider below (which never reliably
            // landed on the same line since it depended on padding/gap adding
            // up correctly instead of a shared fixed height).
            // 62px (was 61px, +1px — Rahim's ask, 2026-08-19, to fix a small
            // remaining seam vs InboxSettings.jsx's own heading divider line).
            // This panel's own outer Box (unlike InboxSettings.jsx's, which has
            // none) has a 1px border wrapping all 4 sides, eating 1px before this
            // row even starts. 62px content + this row's own 1px border-bottom =
            // 63px, landing right after that 1px outer border. Derived from the
            // shared target in inboxLayoutConstants.js — ChatboxNavbar.jsx's 4
            // states derive from the same constant.
            height: {
              xs: "auto",
              sm: "auto",
              md: "auto",
              lg: `${INBOX_ALL_HEADER_TOTAL_PX - INBOX_HEADER_BORDER_PX}px`,
              xl: `${INBOX_ALL_HEADER_TOTAL_PX - INBOX_HEADER_BORDER_PX}px`,
            },
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            // Shown under the Chats/Calls tabs regardless of which section is
            // active (Rahim, 2026-08-20 — it was dropping out entirely on
            // Calls) — only hidden at xs/sm/md, same breakpoints as before.
            borderBottomWidth: { xs: 0, sm: 0, md: 0, lg: 1, xl: 1 },
            // Cancels the wrapper Box's own horizontal padding so the border spans the
            // full panel width instead of stopping short of the edges, while pl/pr below
            // put the row's actual content back at the same visual inset as before.
            mx: { lg: "-14px", xl: "-16px" },
            pl: { lg: "14px", xl: "16px" },
            pr: { lg: "14px", xl: "16px" },
          }}
        >
          {!(searchOpen) && (
            <>
              {/* Reopens the collapsed InboxSettings panel — sits LEFT of the Chats/Calls
                  tabs (Rahim's ask, 2026-08-18, moved from after them), same SidebarLeft
                  Iconsax icon InboxSettings.jsx's own heading uses to close it, so the
                  open/close affordance reads as one consistent icon language instead of
                  a generic hamburger. sm/md always show it (their own drawer toggle);
                  lg/xl only get a truthy onMenuClick (see Inbox.jsx's
                  inboxAllMenuClickHandler) once that panel is actually collapsed —
                  otherwise its own heading icon is the way to close it, so showing both
                  at once would be a redundant/confusing double toggle. */}
              {/* Not gated to inboxSection === "chats" — that left this icon
                  vanishing on the Calls tab whenever the left rail was
                  collapsed, with no way to reopen it from there (Rahim,
                  2026-08-29: "when i close the inbox left rail the open icon
                  sits next to the chats button but when i click on the
                  calls button the button vanish"). Same class of bug as the
                  border fix just above this — reopening the rail isn't tied
                  to which section is active. */}
              {onMenuClick && (
                <IconButton
                  onClick={onMenuClick}
                  sx={{
                    flexShrink: 0,
                    color: (theme) => theme.palette.text.primary,
                    display: "flex",
                    padding: { xs: "3px", sm: "3px", md: "5px", lg: "6px", xl: "7px" },
                    backgroundColor: { xs: "transparent !important", sm: "transparent", md: "transparent", lg: "transparent", xl: "transparent" },
                    borderRadius: { xs: "50%", sm: "50%", md: "50%", lg: "50%", xl: "50%" },
                    height: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                    minHeight: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
                    alignSelf: { xs: "center", sm: "center", md: "center", lg: "center", xl: "center" },
                    boxShadow: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" },
                    "&::before": {
                      display: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" },
                    },
                    "&::after": {
                      display: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" },
                    },
                    "&:hover": {
                      backgroundColor: { xs: (theme) => theme.palette.action.hover, sm: (theme) => theme.palette.action.hover, md: (theme) => theme.palette.action.hover, lg: (theme) => theme.palette.action.hover, xl: (theme) => theme.palette.action.hover },
                    },
                  }}
                >
                  {/* Size matched to InboxSettings.jsx's own heading icon (2026-08-18) — same
                      icon, both ends of the same toggle, should read as one consistent size. */}
                  <SidebarToggleIconIx size="22" color="currentColor" variant="Linear" />
                </IconButton>
              )}

              {/* Chats / Calls section toggle — top-level mode switch. Calls
                  is workspace-wide live state (liveCallsSlice), independent
                  of whichever assignment tab (All/You/Active/...) is
                  selected below, so switching here doesn't touch
                  selectedInboxTab at all. */}
              <Tabs
                value={inboxSection}
                onChange={(e, val) => handleSectionChange(val)}
                // MUI's indicator width/position is computed inline from the active tab's
                // own measured DOM width, so it can't just be shrunk with CSS — this is
                // MUI's own documented pattern for a narrower, centered indicator: render a
                // child span inside the indicator and size/color that instead.
                TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
                sx={{
                  minHeight: 30,
                  flexShrink: 0,
                  // Small nudge down within the row's own fixed-height flex-
                  // centering — NOT padding on the row itself, which would
                  // grow past its carefully fixed height (see the row's own
                  // comment on the 63px InboxSettings-divider alignment) and
                  // break that (Rahim's ask, 2026-08-20 — a little breathing
                  // room above the tabs/search).
                  mt: "3px",
                  "& .MuiTabs-indicator": {
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                  },
                  "& .MuiTabs-indicatorSpan": {
                    maxWidth: 34,
                    width: "100%",
                    height: 3,
                    borderRadius: "2px",
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                }}
              >
                <Tab
                  value="chats"
                  label="Chats"
                  disableRipple
                  sx={{
                    minHeight: 30,
                    minWidth: 0,
                    // MUI's default Tab padding leaves a wide gap before the indicator —
                    // tightened to match the reference underline (Dashboard's ContactsPanel
                    // Assigned/Unassigned/Closed tabs, a 4px gap via `pb: 0.5`).
                    padding: "0px 12px 9px",
                    textTransform: "none",
                    fontSize: "1.05rem",
                    fontWeight: 600,
                    color: (theme) => theme.palette.text.secondary,
                    "&.Mui-selected": { color: (theme) => theme.palette.primary.main },
                  }}
                />
                {callsViewAllowed && (
                  <Tab
                    value="calls"
                    disableRipple
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Calls
                        {liveCallsCount > 0 && (
                          <Badge
                            badgeContent={liveCallsCount}
                            color="error"
                            sx={{ "& .MuiBadge-badge": { position: "static", transform: "none", fontSize: 9, height: 15, minWidth: 15 } }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      minHeight: 30,
                      minWidth: 0,
                      padding: "0px 12px 9px",
                      textTransform: "none",
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: (theme) => theme.palette.text.secondary,
                      "&.Mui-selected": { color: (theme) => theme.palette.primary.main },
                    }}
                  />
                )}
              </Tabs>
            </>
          )}

          <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end", mt: "-2px" }}>
            <InboxSearchToggle
              value={inboxSection === "chats" ? searchTerm : callsSearchTerm}
              onChange={inboxSection === "chats" ? setSearchTerm : setCallsSearchTerm}
              open={searchOpen}
              onOpenChange={setSearchOpen}
              placeholder={inboxSection === "chats" ? "Search for Contacts" : "Search calls"}
              iconSize={22}
              // Calls search is local-only (filters teamMemberChats/etc.
              // client-side already, no backend call to gate behind Enter).
              onSubmit={inboxSection === "chats" ? handleSearchSubmit : undefined}
            />
          </Box>
        </Box>

        {/* Filter summary bar (Show/Sort dropdown) + independent Unreplied
            toggle — stays visible even while the search overlay above is
            open, same as the reference design. */}
        {inboxSection === "chats" && bulkSelectingNow && (
          <Box sx={{ pl: "8px", pr: "4px" }}>
            <InboxBulkActionBar selectedInboxTab={selectedInboxTab} />
          </Box>
        )}
        {/* Always mounted (socket + polling follow the job even while the strip is hidden). */}
        <InboxBulkJobTracker showStrip={inboxSection === "chats" && bulkJobStripVisible} />
        {inboxSection === "chats" && !bulkSelectingNow && !bulkJobStripVisible && (
          // pl:"12px" matches the "Chats" Tab's own internal left padding above
          // (padding:"0px 12px 9px") — without it "All, Oldest" starts flush at
          // this row's own edge while "Chats" sits 12px further in from its own
          // Tab padding, reading as visibly misaligned (Rahim, 2026-08-19).
          <Box display="flex" justifyContent="space-between" alignItems="center" pl="12px">
            <InboxFilterDropdown
              showValue={activeFilter}
              onShowChange={(v) => setActiveFilter(v === "all" ? null : v)}
              showOptions={[
                { value: "all", label: "All conversations" },
                { value: "unread", label: "Unread" },
                { value: "opened", label: "Open" },
                { divider: true },
                // QR chats are their own platform now. "WhatsApp" = the official (Meta) number; it is hidden for a
                // QR-only workspace (it would always be empty there) and "WhatsApp (QR)" only shows when QR numbers exist.
                ...(isQrConnectedWorkspace ? [] : [{ value: "whatsapp", label: "WhatsApp" }]),
                ...(Array.isArray(currentWorkspace?.whatsappQrConnections) && currentWorkspace.whatsappQrConnections.length > 0
                  ? [{ value: "qr", label: "WhatsApp (QR)" }]
                  : []),
                { value: "facebook", label: "Facebook" },
                { value: "instagram", label: "Instagram" },
                { value: "website", label: "Web Chat" },
              ]}
              sortValue={sortOrder}
              onSortChange={setSortOrder}
              sortOptions={[
                { value: "newest", label: "Newest" },
                { value: "oldest", label: "Oldest" },
              ]}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {/* Touch screens have no avatar hover to reveal the row checkboxes —
                this enters multi-select mode explicitly instead. */}
            {isTouchOnly && bulkSelectionAvailable && (
              <Chip
                clickable
                size="small"
                label="Select"
                onClick={() => dispatch(enterBulkSelectionMode())}
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  borderRadius: "8px",
                  color: (theme) => theme.palette.text.secondary,
                  borderColor: (theme) => theme.palette.divider,
                  "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
                }}
              />
            )}
            {/* A filled/outlined pill, not a bare Switch — reads as a real
                filter chip (consistent with how the rest of the filter row
                already looks) instead of a generic settings toggle. */}
            <Chip
              clickable
              size="small"
              icon={<MarkChatUnreadOutlinedIcon sx={{ fontSize: "13px !important" }} />}
              label="Unreplied"
              onClick={() => setUnrepliedOnly((prev) => !prev)}
              variant={unrepliedOnly ? "filled" : "outlined"}
              sx={{
                // Made smaller (was height:26, fontSize:0.875rem) — Rahim, 2026-08-19.
                height: 22,
                fontSize: "0.75rem",
                fontWeight: 400,
                borderRadius: "8px",
                transition: "background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
                ...(unrepliedOnly
                  ? {
                      backgroundColor: (theme) => theme.palette.primary.main,
                      color: (theme) => theme.palette.primary.contrastText,
                      boxShadow: "0 1px 5px rgba(14, 144, 70, 0.4)",
                      "& .MuiChip-icon": { color: (theme) => theme.palette.primary.contrastText },
                      // MUI's default clickable-filled Chip hover is a light
                      // overlay meant for its default grey chip — without an
                      // explicit override here it showed as a washed-out
                      // white tint on top of this custom green fill (Rahim,
                      // 2026-08-31: "when hoered it shows weird white
                      // colour").
                      "&:hover": { backgroundColor: (theme) => theme.palette.primary.dark },
                    }
                  : {
                      color: (theme) => theme.palette.text.secondary,
                      borderColor: (theme) => theme.palette.divider,
                      "& .MuiChip-icon": { color: (theme) => theme.palette.text.secondary },
                      "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
                    }),
              }}
            />
            </Box>
          </Box>
        )}
      </Box>
      {/* Active Members */}
      {/* <Typography
        variant="h5"
        ml={1}
        mb={0.5}
        padding={0.5}
        sx={{ color: "black", fontWeight: "500" }}
      >
        Active Members
      </Typography> */}
      {/* <Box sx={{ display: "flex", alignItems: "center", mb: 1, paddingX: 1 }}> */}
      {/* Scroll Left Button */}
      {/* <IconButton onClick={handleScrollLeft} sx={{ p: 0 }}>
          <LeftScroll width={20} height={20} />
        </IconButton> */}

      {/* Scrollable Active Members Box */}
      {/* <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            gap: 0.5,
            alignItems: "center",
            flex: 1, // Take up available space
          }}
          ref={scrollRef}
        >
          {loading && (
            <Box>
              {" "}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  padding: 2,
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Box
                    key={item}
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Skeleton variant="circular" width={40} height={40} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {!loading &&
            activeMembers?.map((member, index) => (
              <Box key={index} textAlign="center" minWidth={80}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    width: 48,
                    height: 48,
                  }}
                > */}
      {/* Avatar */}
      {/* <Avatar
                    sx={{
                      background:
                        "radial-gradient(circle, white 53%, #1BB5EC 50%)", // Gradient background
                      width: 48,
                      height: 48,
                      fontSize: "1.2rem",
                      color: "black", // Text color
                      fontWeight: 600, // Bolder text
                    }}
                  >
                    {member ? member.name[0] : ""}
                  </Avatar> */}
      {/* Active green dot */}
      {/* <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: 12,
                      height: 12,
                      bgcolor: "#00e676",
                      borderRadius: "50%",
                      border: "3px solid white", // White border for separation
                    }}
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "black",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    width: "80px",
                  }}
                >
                  {member ? member.name : ""}
                </Typography>
              </Box>
            ))}
        </Box> */}

      {/* Right Scroll Button */}
      {/* <IconButton onClick={handleScrollRight} sx={{ p: 0 }}>
          <RightScroll width={20} height={20} />
        </IconButton> */}
      {/* </Box> */}

      {/* <Divider sx={{ color: "main.secondary" }} /> */}
      {/* Recent Messages */}
      <Box
        sx={{
          flex: 1, // Fill remaining space instead of fixed height
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevent scrolling on the parent
          marginBottom: 0,
          paddingBottom: 0,
          minHeight: 0, // Important for flex child to shrink properly
        }}
      >
        <Box
          sx={{
            flex: 1, // Makes this Box fill the remaining space
            overflow: "hidden", // VirtualList handles its own scrolling
            position: "relative", // Added for proper virtualization container
            minHeight: 0, // Important for flex child to shrink properly
            width: "100%", // Ensure full width
            marginBottom: 0,
            paddingBottom: 0,
            paddingRight: 0,
            display: "flex", // Ensure flex container for MessageCard
            flexDirection: "column", // Column direction for proper layout
            // Style scrollbar for VirtualList - target all scrollable children
            '& > *': {
              flex: 1, // MessageCard should fill available space
              minHeight: 0, // Allow MessageCard to shrink
              width: "100%",
            },
            '& > * > div': {
              // Shared formula (utils/scrollbarStyles.js) — single source of
              // truth, matches Dashboard.jsx exactly.
              ...slimScrollbarSx,
            },
          }}
        >
          {inboxSection === "calls" ? (
            <CallsSectionBody searchValue={callsSearchTerm} />
          ) : (
            <>
          {/* Skeleton loader while the inbox fetch is still in flight (or hasn't
              run yet) — shape matches MessageCard.jsx's real row exactly (same
              50px avatar, minHeight, padding), not a generic placeholder. The
              old version (40px avatar, 2 text lines, py:1) was left behind
              when that row got redesigned, so the skeleton→real-content swap
              visibly jumped in height (Rahim, 2026-08-20). */}
          {!isChatWithMembersTab && contactsToShow.length === 0 && (isLoadingInbox || !inboxInitialized) && (
            <Box>
              {[...Array(6)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: { xs: "6px", sm: "5px", md: "8px", lg: "10px", xl: "11px" },
                    pt: 1.75,
                    pb: 1.75,
                    minHeight: { xs: "115px", sm: "88px", md: "115px", lg: "115px", xl: "115px" },
                    boxSizing: "border-box",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Skeleton variant="circular" width={50} height={50} sx={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="60%" height={18} />
                    <Skeleton variant="text" width="45%" height={14} sx={{ mt: 0.5 }} />
                    <Skeleton variant="rounded" width={64} height={18} sx={{ mt: 1, borderRadius: "6px" }} />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75, flexShrink: 0 }}>
                    <Skeleton variant="text" width={40} height={14} />
                    <Skeleton variant="rounded" width={56} height={18} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {/* Pass searchTerm, selectedLabels, and activeFilter as props, and contactsToShow as data */}
          {isChatWithMembersTab ? (
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {/* Active Team Member Chats */}
              {activeTeamMemberChats.length > 0 && (
                <>
                  {activeTeamMemberChats.map((chat, index) => {
                    const isSelected = currentTeamMemberChat?.otherMemberId === chat.otherMemberId;
                    return (
                  <Box
                    key={chat.id}
                    onClick={() => handleTeamMemberChatClick(chat)}
                    sx={{
                      display: "flex",
                          justifyContent: "space-between",
                      alignItems: "center",
                          padding: { xs: "4px 6px", sm: "3px 5px", md: "5px 8px", lg: "6px 10px", xl: "7px 11px" },
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          // Matches MessageCard.jsx's own selected-row style (neutral grey
                          // rgba, no colored border bar) instead of the old primary-color
                          // right-border + theme.palette.action.selected/hover pair — those
                          // two rows had drifted onto a different, older convention (Rahim,
                          // 2026-08-27: "the clicked effect is the past as well as hover").
                          backgroundColor: isSelected
                            ? (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
                            : "transparent",
                          minHeight: { xs: "70px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                          position: "relative",
                          overflow: "hidden",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: (theme) =>
                          isSelected
                            ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"
                            : theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
                      },
                        }}
                      >
                        <Box sx={{ display: "flex", flex: 1, alignItems: "center", minWidth: 0 }}>
                          {/* Avatar Section */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: { xs: 1, sm: 1, md: 1.25, lg: 1.5, xl: 1.5},
                              minWidth: "48px",
                    }}
                  >
                            <Avatar
                              {...stringAvatar(chat.name)}
                              sx={{
                                bgcolor: chat.avatarColor,
                                color: "Black", // Ensure text color is black like user conversations
                                width: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
                                height: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
                                fontSize: { xs: "16px", sm: "12.8px", md: "16px", lg: "16px", xl: "16px" },
                              }}
                            />
                          </Box>

                          {/* Content Section */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              width: "100%",
                              gap: { xs: 0.5, sm: 0.25, md: 0.5, lg: 0.5, xl: 0.5 },
                              marginLeft: 0,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                maxWidth: "140px",
                                width: "100%",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                // 600, not "bold"/700 — matches MessageCard.jsx's own row-title
                                // weight (Rahim, 2026-08-27: "other fonts being used" too bold).
                                fontWeight="600"
                                sx={{
                                  color: (theme) => theme.palette.text.primary,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: { xs: "0.95rem", sm: "0.8125rem", md: "0.95rem", lg: "0.95rem", xl: "0.95rem" },
                                  lineHeight: 1.2,
                                }}
                              >
                          {chat.name}
                        </Typography>
                      </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                                color: (theme) => theme.palette.text.secondary,
                                whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                                maxWidth: { xs: "120px", sm: "90px", md: "125px", lg: "145px", xl: "155px" },
                                fontSize: { xs: "0.75rem", sm: "0.5625rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                                lineHeight: 1.2,
                                marginBottom: { xs: 0.5, sm: 0.25, md: 0.5, lg: 0.5, xl: 0.5 },
                          }}
                        >
                          {chat.lastMessage}
                        </Typography>
                          </Box>
                        </Box>

                        {/* Right Section - Timestamp and Unread Count */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            justifyContent: "flex-start",
                            gap: 0.5,
                            marginLeft: 1,
                            flexShrink: 0,
                          }}
                        >
                          {chat.timestamp && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                fontSize: { xs: "0.625rem", sm: "0.5rem", md: "0.625rem", lg: "0.6875rem", xl: "0.75rem" },
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatMessageTime(chat.timestamp)}
                            </Typography>
                          )}
                        {chat.unreadCount > 0 && (
                          <Box
                            sx={{
                              bgcolor: "primary.main",
                              color: "white",
                              borderRadius: "50%",
                              // Fixed width/height (not minWidth + padding) forces a true circle
                              // ALWAYS, even for "99+" — Rahim's explicit call: a perfect circle
                              // with "99+" touching the edge beats a pill shape. Same fix as
                              // InboxSettings.jsx's COUNT_BADGE_SX.
                              width: 22,
                              height: 22,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                                fontSize: { xs: "0.625rem", sm: "0.5rem", md: "0.625rem", lg: "0.6875rem", xl: "0.75rem" },
                              fontWeight: 600,
                            }}
                          >
                            {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    );
                  })}
                </>
              )}

              {/* Team Members Heading - Show only if there are members without chats.
                  borderTop removed (Rahim, 2026-08-27: "the top border is doubled") — the
                  last active-chat row right above this already has its own borderBottom, so
                  adding a borderTop here stacked a second 1px line directly on top of it with
                  zero gap, reading as one thick double-border. borderBottom alone still
                  cleanly separates this heading from the member rows below it. */}
              {teamMembersWithoutChats.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: (theme) => `1px solid ${theme.palette.divider}`, px: 1 }}>
                  {/* fontSize/fontWeight now match InboxSettings.jsx's own "Team Member"/
                      "Teams"/"Lifecycle" section-header convention exactly (0.9375rem/600)
                      instead of the old h5-variant + xl-only 700/1.25rem outlier (Rahim,
                      2026-08-27: "the heading team members way too damn bold"). */}
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      fontWeight: 600,
                      fontSize: "0.9375rem",
                      paddingBottom: 0,
                      lineHeight: "1",
                      paddingLeft: { xs: "8px", sm: "6px", md: "8px", lg: "2px", xl: "2px" },
                      paddingTop: { xs: "2px", sm: "0px", md: "2px", lg: "2px", xl: "2px" },
                      paddingRight: { xs: "8px", sm: "6px", md: "8px", lg: "17px", xl: "17px" },
                      display: "flex",
                      alignItems: "center",
                      height: { xs: "32px", sm: "21px", md: "34px", lg: "36px", xl: "40px" },
                    }}
                  >
                    Team Members
                  </Typography>
                  </Box>
              )}

              {/* Team Members Without Chats */}
              {teamMembersWithoutChats.length > 0 && (
                <>
                  {teamMembersWithoutChats.map((chat) => {
                    const isSelected = currentTeamMemberChat?.otherMemberId === chat.otherMemberId;
                    return (
                      <Box
                        key={chat.id}
                        onClick={() => handleTeamMemberChatClick(chat)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: { xs: "4px 6px", sm: "3px 5px", md: "5px 8px", lg: "6px 10px", xl: "7px 11px" },
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          // Same MessageCard.jsx-matching selected/hover fix as the active-
                          // chats block above (Rahim, 2026-08-27).
                          backgroundColor: isSelected
                            ? (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
                            : "transparent",
                          minHeight: { xs: "70px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                          position: "relative",
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: (theme) =>
                              isSelected
                                ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"
                                : theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", flex: 1, alignItems: "center", minWidth: 0 }}>
                          {/* Avatar Section */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: { xs: 1, sm: 1, md: 1.25, lg: 1.5, xl: 1.5},
                              minWidth: "48px",
                            }}
                          >
                            <Avatar
                              {...stringAvatar(chat.name)}
                              sx={{
                                bgcolor: chat.avatarColor,
                                color: "Black", // Ensure text color is black like user conversations
                                width: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
                                height: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
                                fontSize: { xs: "16px", sm: "12.8px", md: "16px", lg: "16px", xl: "16px" },
                              }}
                            />
                          </Box>

                          {/* Content Section */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              width: "100%",
                              gap: { xs: 0.5, sm: 0.25, md: 0.5, lg: 0.5, xl: 0.5 },
                              marginLeft: 0,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                maxWidth: "140px",
                                width: "100%",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                fontWeight="600"
                                sx={{
                                  color: (theme) => theme.palette.text.primary,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: { xs: "0.95rem", sm: "0.8125rem", md: "0.95rem", lg: "0.95rem", xl: "0.95rem" },
                                  lineHeight: 1.2,
                                }}
                              >
                                {chat.name}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: { xs: "120px", sm: "90px", md: "125px", lg: "145px", xl: "155px" },
                                fontSize: { xs: "0.75rem", sm: "0.5625rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                                lineHeight: 1.2,
                                marginBottom: { xs: 0.5, sm: 0.25, md: 0.5, lg: 0.5, xl: 0.5 },
                              }}
                            >
                              {chat.lastMessage}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* Empty State */}
              {activeTeamMemberChats.length === 0 && teamMembersWithoutChats.length === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", px: 4 }}>
                  <Box sx={{ mb: 1.5, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24" fill="none">
                      <g>
                        <path d="M9.16006 10.87C9.06006 10.86 8.94006 10.86 8.83006 10.87C6.45006 10.79 4.56006 8.84 4.56006 6.44C4.56006 3.99 6.54006 2 9.00006 2C11.4501 2 13.4401 3.99 13.4401 6.44C13.4301 8.84 11.5401 10.79 9.16006 10.87Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path opacity="0.4" d="M16.41 4C18.35 4 19.91 5.57 19.91 7.5C19.91 9.39 18.41 10.93 16.54 11C16.46 10.99 16.37 10.99 16.28 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4.15997 14.56C1.73997 16.18 1.73997 18.82 4.15997 20.43C6.90997 22.27 11.42 22.27 14.17 20.43C16.59 18.81 16.59 16.17 14.17 14.56C11.43 12.73 6.91997 12.73 4.15997 14.56Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path opacity="0.4" d="M18.34 20C19.06 19.85 19.74 19.56 20.3 19.13C21.86 17.96 21.86 16.03 20.3 14.86C19.75 14.44 19.08 14.16 18.37 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    </svg>
                  </Box>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    No team members found
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
          <MessageCard
            // committedSearchTerm, not the raw live searchTerm -- only used
            // here for the "no results" message text, set on Enter
            // (2026-09-08).
            searchTerm={committedSearchTerm}
            // Drives the centered spinner (replacing the empty-state icon)
            // while the real DB search is in flight -- distinct from
            // isLoadingMore below, which is the ordinary bottom-of-list
            // pagination indicator, unrelated to search.
            isSearching={isSearchMode && isSearching}
            selectedLabels={selectedLabels}
            contactsData={contactsToShow}
            activeFilter={activeFilter}
            unrepliedOnly={unrepliedOnly}
            hasMore={!isSearchMode && currentTabHasMore && contactsToShow.length < MAX_AUTO_SCROLL}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
            showLoadMoreButton={!isSearchMode && showLoadMoreButton}
            onExplicitLoadMore={handleExplicitLoadMore}
            isBlockedTab={selectedInboxTab === "Blocked Contacts"}
          />
          )}
            </>
          )}
        </Box>
      </Box>

      {/* Action buttons positioned at bottom right - Hide for team members chat and the Calls section */}
      {!isChatWithMembersTab && inboxSection === "chats" && (
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: "30px", sm: "30px", md: 20, lg: 20, xl: 20 },
          right: 20,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          zIndex: 10,
        }}
      >
        {/* Filter button with badge showing count of active filters */}
          <IconButtonWithTooltip
            label={
              !LabelPermissions.View
                ? "You don't have permission to filter by labels"
                : "Filter by labels"
            }
            onClick={handleFilterOpen}
            sx={{
              backgroundColor: "white",
              color: "#333",
              width: { xs: 32, sm: 32, md: 36, lg: 40, xl: 44 },
              height: { xs: 32, sm: 32, md: 36, lg: 40, xl: 44 },
              borderRadius: "12px",
              padding: { xs: "6px", sm: "6px", md: "8px", lg: "10px", xl: "12px" },
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              },
              "&.Mui-disabled": {
                backgroundColor: (theme) =>
                  theme.palette.action.disabledBackground,
                color: (theme) => theme.palette.action.disabled,
                boxShadow: "none",
              },
              "& svg": {
                width: { xs: 14, sm: 14, md: 16, lg: 18, xl: 18 },
                height: { xs: 14, sm: 14, md: 16, lg: 18, xl: 18 },
              },
            }}
            disabled={!LabelPermissions.View}
          >
            <FilterButton style={{ width: "100%", height: "100%" }} />
            {selectedLabels.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: (theme) => theme.palette.error.main,
                  borderRadius: "50%",
                  width: { xs: 14, sm: 14, md: 16, lg: 18, xl: 20 },
                  height: { xs: 14, sm: 14, md: 16, lg: 18, xl: 20 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: { xs: "0.5625rem", sm: "0.5625rem", md: "0.625rem", lg: "0.6875rem", xl: "0.6875rem" },
                  fontWeight: "bold",
                }}
              >
                {selectedLabels.length}
              </Box>
            )}
          </IconButtonWithTooltip>

        {/* Add new contact button */}
        <IconButton
          onClick={handleCreateContactOpen}
          sx={{
            backgroundColor: "#4CAF50",
            color: "white",
            width: { xs: 36, sm: 36, md: 40, lg: 48, xl: 52 },
            height: { xs: 36, sm: 36, md: 40, lg: 48, xl: 52 },
            borderRadius: "12px",
            padding: { xs: "10px", sm: "10px", md: "16px", lg: "20px", xl: "24px" },
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
            "&:hover": {
              backgroundColor: "#45a049",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <AddIcon sx={{ fontSize: { xs: 16, sm: 16, md: 18, lg: 20, xl: 22 } }} />
        </IconButton>
      </Box>
      )}

      {/* Filter Label Modal */}
      <Dialog
        open={filterOpen}
        onClose={handleFilterClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            padding: 0,
            maxHeight: "72vh",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // Was 24px/bold(700) at md+ — well past this app's actual heading
            // scale (compare MessageCard.jsx's own confirm-dialog title,
            // 1.2rem/600); toned down further after Rahim flagged it as still
            // too bold/big (2026-08-20).
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
            padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
            marginBottom: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          Filter by Labels
          <IconButton
            onClick={handleFilterClose}
            size="small"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
              "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "22px", xl: "22px" } },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
            pb: { xs: 1, sm: 1.25, md: 1.5, lg: 1.5, xl: 1.5 },
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            // MUI sets DialogContent paddingTop to 0 when it follows DialogTitle; add space below divider
            <Box sx={{ pt: { xs: 2, sm: 2.25, md: 2.5, lg: 2.5, xl: 2.5 } }}>
              {/* Selected-label pills — soft tinted fill instead of a plain
                  outlined Chip, matching the soft-pill convention used
                  everywhere else this session (status pills, Lifecycle chip). */}
              {selectedLabels.length > 0 && (
                <Box sx={{ mb: 1.75, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedLabels.map((label, index) => (
                    <Chip
                      key={index}
                      size="small"
                      label={label}
                      onDelete={() => handleLabelToggle(label)}
                      deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                      sx={{
                        height: 22,
                        // Tint softened + a step darker text — was bright/heavy
                        // green at full Chip size, read as too big/loud
                        // (Rahim, 2026-08-20).
                        bgcolor: (theme) => (theme.palette.mode === "dark" ? `${theme.palette.primary.main}30` : `${theme.palette.primary.main}14`),
                        color: (theme) => (theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark),
                        fontWeight: 600,
                        fontSize: "0.6875rem",
                        border: "none",
                        // MUI Chip doesn't truncate its own label by default —
                        // a long user-created label name would just stretch
                        // the pill very wide. Capped + ellipsized (Rahim,
                        // 2026-08-20 — labels are free text, must handle this).
                        maxWidth: 160,
                        "& .MuiChip-label": { px: 0.875, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                        "& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.6, ml: 0, mr: "4px", "&:hover": { opacity: 1 } },
                      }}
                    />
                  ))}
                  <Chip
                    size="small"
                    label="Clear All"
                    onClick={clearLabelFilter}
                    sx={{
                      height: 22,
                      bgcolor: "transparent",
                      color: (theme) => theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: "0.6875rem",
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      "& .MuiChip-label": { px: 0.875 },
                      "&:hover": { bgcolor: (theme) => theme.palette.action.hover },
                    }}
                  />
                </Box>
              )}

              {Array.isArray(labels) && labels.length > 0 ? (
                // Whole row is clickable (not just the checkbox/text), rounded
                // hover — same DROPDOWN_ITEM_SX shape every other menu/list row
                // in the Inbox module uses, instead of a plain FormControlLabel.
                <Box>
                  {labels.map((label, index) => {
                    const checked = selectedLabels.includes(label);
                    return (
                      // ButtonBase (not a plain Box) — was mouse-only, no
                      // tabIndex/keyboard handling at all (Rahim, 2026-08-20:
                      // "check best frontend practices"). ButtonBase gives
                      // native Tab-focus + Enter/Space activation for free.
                      <ButtonBase
                        key={index}
                        onClick={() => handleLabelToggle(label)}
                        sx={{
                          ...DROPDOWN_ITEM_SX,
                          // DROPDOWN_ITEM_SX's own py/mb (0.85/0.25) are tuned
                          // for a compact dropdown menu — bumped for this
                          // checklist, which needs more breathing room
                          // between rows (Rahim, 2026-08-20). px zeroed out —
                          // DialogContent already provides the 20px inset;
                          // DROPDOWN_ITEM_SX's own px:1.5 stacked on top of
                          // that, pushing every row ~12px further right than
                          // the "Filter by Labels" heading above it.
                          py: 1.1,
                          // Same "rounded floating pill, inset from the rail's
                          // edges" formula InboxSettings.jsx's own left-rail
                          // rows use (mx:1, not action.hover but its specific
                          // rgba formula) — that's the reference Rahim pointed
                          // to directly (2026-08-20) after two smaller-mx
                          // attempts still read as touching the edge.
                          px: 1.25,
                          mx: 1,
                          mb: 1,
                          width: "calc(100% - 16px)",
                          borderRadius: "8px",
                          justifyContent: "flex-start",
                          // Bumped from 0.9rem — read a touch small next to
                          // the pills above it.
                          fontSize: "1rem",
                          color: (theme) => theme.palette.text.primary,
                          transition: "background-color 0.12s ease",
                          "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() => handleLabelToggle(label)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ p: 0 }}
                        />
                        <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0, ml: 0 }}>
                          <TagIconIx size="100%" color="currentColor" variant="Linear" />
                        </Box>
                        {/* Raw {label} text had zero overflow handling before
                            — a long user-created label name would just wrap/
                            overflow unpredictably (Rahim, 2026-08-20: labels
                            are free text, must handle this). */}
                        <Box component="span" sx={{ minWidth: 0, flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {label}
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.secondary", opacity: 0.4 }}>
                    <TagIconIx size="40" color="currentColor" variant="Linear" />
                  </Box>
                  <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", fontWeight: 500 }}>No labels available</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.disabled", mt: 0.5 }}>
                    Labels created in Settings will show up here.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 14px", sm: "12px 18px 16px", md: "14px 20px 18px", lg: "14px 20px 18px", xl: "14px 20px 18px" },
            justifyContent: "space-between",
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {/* Font sizes normalized to 0.875rem (14px) flat — was jumping up
              to a full 16px only at md+, out of step with the 0.8125-0.9rem
              scale the rest of this dialog's redesign now uses (Rahim,
              2026-08-20). */}
          <Button
            onClick={handleFilterClose}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 500,
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={applyLabelFilter}
            disabled={selectedLabels.length === 0}
            sx={(theme) => ({
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "18px",
              textTransform: "none",
              fontWeight: 600,
              padding: "5px 14px",
              fontSize: "0.8125rem",
              boxShadow: theme.palette.mode === "dark" ? "0 1px 0 rgba(255,255,255,0.06) inset" : "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              "&.Mui-disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            })}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contact Modal */}
      <Dialog
        open={createContactOpen}
        onClose={handleCreateContactClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            padding: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // Same redesign pass as "Filter by Labels" — was 24px/bold(700),
            // well past this app's actual heading scale (Rahim, 2026-08-20).
            fontWeight: 600,
            fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
            padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
            marginBottom: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          Create New Contact
          <IconButton
            onClick={handleCreateContactClose}
            size="small"
            aria-label="Close"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
              "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "22px", xl: "22px" } },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 },
            pb: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
            flex: "0 1 auto",
            overflowY: "visible",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, sm: 3.25, md: 3.5, lg: 3.5, xl: 3.5 },
              pt: { xs: 2.5, sm: 3, md: 3.25, lg: 3.25, xl: 3.25 },
              pb: { xs: 1, sm: 1.25, md: 1.5, lg: 1.5, xl: 1.5 },
            }}
          >
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              size="medium"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  // Font/height normalized to this dialog-redesign pass's
                  // 0.875rem scale — was 16px at md+, oversized next to
                  // everything else this session (Rahim, 2026-08-20).
                  minHeight: { xs: 44, sm: 45, md: 46, lg: 46, xl: 46 },
                  paddingY: { xs: "4px", sm: "6px" },
                  fontSize: "0.875rem",
                  "& fieldset": {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: (theme) => theme.palette.text.secondary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.8125rem",
                  color: (theme) => theme.palette.text.secondary,
                },
                "& .MuiOutlinedInput-input": {
                  color: (theme) => theme.palette.text.primary,
                  py: { xs: "10px", sm: "11px" },
                },
              }}
            />

            <TextField
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                const val = e.target.value;
                setPhone(val);
                if (!val) {
                  setPhoneError("");
                  return;
                }
                const res = validateAndNormalizePhone(val);
                setPhoneError(res.ok ? "" : res.message);
              }}
              fullWidth
              required
              error={!!phoneError}
              helperText={
                phoneError ||
                "Enter number in international format, e.g. +12025550123"
              }
              size="medium"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  minHeight: { xs: 44, sm: 45, md: 46, lg: 46, xl: 46 },
                  paddingY: { xs: "4px", sm: "6px" },
                  fontSize: "0.875rem",
                  "& fieldset": {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: (theme) => theme.palette.text.secondary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.8125rem",
                  color: (theme) => theme.palette.text.secondary,
                },
                "& .MuiOutlinedInput-input": {
                  color: (theme) => theme.palette.text.primary,
                  py: { xs: "10px", sm: "11px" },
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.78)"
                      : theme.palette.text.secondary,
                  marginTop: "12px",
                  "&.Mui-error": {
                    color: (theme) => theme.palette.error.main,
                  },
                },
              }}
            />

            <ContactChannelPicker channelState={contactChannel} />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "12px 16px 16px", sm: "14px 20px 18px", md: "16px 20px 20px", lg: "16px 20px 20px", xl: "16px 20px 20px" },
            justifyContent: "space-between",
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {/* Same button redesign as "Filter by Labels" — sizes/padding
              normalized (Rahim, 2026-08-20). */}
          <Button
            onClick={handleCreateContactClose}
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textTransform: "none",
              fontSize: "0.8125rem",
              fontWeight: 500,
              borderRadius: "8px",
              px: 1.25,
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateContact}
            disabled={
              !name || !phone || !!phoneError || !ContactPermissions.Add || !contactChannel.ready
            }
            sx={(theme) => ({
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              // Was a full pill (18px) — every other button this session
              // (Close, Assign to AI, Send) uses the same 8px radius; the
              // pill shape read as an older, unrelated convention next to
              // them (Rahim, 2026-08-29: "fix this card's ui to match our
              // new style... button").
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: { xs: 1.375, sm: 1.75 },
              py: { xs: 0.625, sm: 0.75 },
              fontSize: "0.8125rem",
              boxShadow: theme.palette.mode === "dark" ? "0 1px 0 rgba(255,255,255,0.06) inset" : "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              "&.Mui-disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            })}
          >
            Create & Chat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InboxAll;